import CustomCard from "../../components/reusable/CustomCard";
import classes from "./RMRCheatsheetPage.module.css";
import React, { ReactNode } from "react";
import cardiovascularIcon from "../../images/cheatsheet/cardiovascularIcon.png";
import cognitiveIcon from "../../images/cheatsheet/cognitiveIcon.png";
import respiratoryIcon from "../../images/cheatsheet/respiratoryIcon.png";
import metabolicIcon from "../../images/cheatsheet/metabolicIcon.png";
import arrowUpIcon from "../../images/cheatsheet/arrowUpIcon.png";
import arrowDownIcon from "../../images/cheatsheet/arrowDownIcon.png";
import Layout from "../../components/reusable/Layout";
import ServiceSection from "../UserServicesPage/ServiceSection";
import nadNrImage from "../../images/nadNr.jpg";

const restoreNadServiceData = {
  TITLE: "NAD+/NR",
  INDEX: 0,
  IMAGESMALL_URL: nadNrImage,
  ACTIVE_INGREDIENT_LABEL: "Active Ingredient",
  ACTIVE_INGREDIENT: "NAD+/NR",
  PNOE_BENEFITS_LABEL: "Metrics benefited",
  ADDITIONAL_BENEFITS_LABEL: "Additional benefits",
  PNOE_BENEFITS: [
    { INDEX: 0, VALUE: "Recovery capacity" },
    { INDEX: 1, VALUE: "Cardiovascular fitness" },
    { INDEX: 2, VALUE: "Breathing cognition" },
    { INDEX: 3, VALUE: "Heart fitness" },
  ],
  ADDITIONAL_BENEFITS: [
    { INDEX: 0, VALUE: "Participates in DNA maintenance and repair" },
    { INDEX: 1, VALUE: "Reduces oxidative stress and inflammation" },
    { INDEX: 2, VALUE: "Maintains a healthy nervous system" },
    { INDEX: 3, VALUE: "Regulates energy metabolism" },
    { INDEX: 4, VALUE: "Influences immune cell function" },
  ],
};

const getColorCodingText = (type: number) => {
  switch (type) {
    case 3:
      return {
        color: "var(--Severe-limitation)",
        fontFamily: "AktivGroteskEx_A_Bd",
      };
    case 2:
      return { color: "var(--Neutral)", fontFamily: "AktivGroteskEx_A_Bd" };
    case 1:
      return { color: "var(--Good)", fontFamily: "AktivGroteskEx_A_Bd" };
    default:
      return {};
  }
};

interface TableSectionProps {
  title: string;
  children: ReactNode;
  marginBottom?: string;
  titleFontSize?: string;
  dataAgentTarget?: string;
}

const TableSection: React.FC<TableSectionProps> = ({
  title,
  children,
  marginBottom = "32px",
  titleFontSize,
  dataAgentTarget,
}) => {
  return (
    <div style={{ marginBottom }} data-agent-target={dataAgentTarget}>
      <div
        data-agent-target={`${dataAgentTarget}|title`}
        className={classes.title}
        style={titleFontSize ? { fontSize: titleFontSize } : {}}
      >
        {title}
      </div>
      {children}
    </div>
  );
};

const CheatsheetCustomCard = ({ children }: { children: React.ReactNode }) => {
  return (
    <CustomCard cardMargin={"0"} cardPadding={"0 0 8px 0"} cardWidth={"100%"}>
      {children}
    </CustomCard>
  );
};

interface TableHeaderProps {
  headers: {
    label: string;
    flex?: number;
  }[];
  dataAgentTarget?: string;
}

const TableHeader: React.FC<TableHeaderProps> = ({
  headers,
  dataAgentTarget,
}) => {
  return (
    <div
      className={classes.header}
      data-agent-target={`${dataAgentTarget}|table_header`}
    >
      {headers.map((header, index) => (
        <div
          key={index}
          className={classes.headerCell}
          style={header.flex ? { flex: header.flex } : {}}
        >
          {header.label}
        </div>
      ))}
    </div>
  );
};

// Map system type to icon
const getSystemImage = (type: number) => {
  switch (type) {
    case 1:
      return cognitiveIcon;
    case 2:
      return respiratoryIcon;
    case 3:
      return cardiovascularIcon;
    case 4:
      return metabolicIcon;
    default:
      return cardiovascularIcon;
  }
};

const SystemOverviewTable = ({
  data,
  dataAgentTarget,
}: {
  data: any;
  dataAgentTarget?: string;
}) => {
  // Check if data or table is missing or empty
  if (!data || !data.table || data.table.length === 0) return null;

  const headers = [
    { label: data.system },
    { label: data.evaluation },
    { label: data.summary },
  ];

  return (
    <TableSection title={data.title} dataAgentTarget={dataAgentTarget}>
      <CheatsheetCustomCard>
        <TableHeader headers={headers} dataAgentTarget={dataAgentTarget} />
        {data.table.map((row: any, index: number) => (
          <div
            key={index}
            className={classes.row}
            data-agent-target={`${dataAgentTarget}|row_${row.Name.toLowerCase().replace(
              / /g,
              "_"
            )}`}
          >
            <div className={`${classes.tableCell} ${classes.tableCellRow}`}>
              <img
                src={getSystemImage(row.type)}
                alt="System Icon"
                style={{ width: "24px", height: "24px" }}
              />
              <div>{row.Name}</div>
            </div>
            <div className={`${classes.tableCell} ${classes.tableCellColumn}`}>
              <div style={getColorCodingText(row.EvaluationType)}>
                {row.Evaluation}
              </div>
              {row.evaluationDetails &&
                row.evaluationDetails.map((detail: any, index: number) => (
                  <div key={index}>{detail}</div>
                ))}
            </div>
            <div className={classes.tableCell}>{row.Summary}</div>
          </div>
        ))}
      </CheatsheetCustomCard>
    </TableSection>
  );
};

interface ExperienceTableProps {
  data: any;
  isImpact?: boolean;
  dataAgentTarget?: string;
}

const ExperienceSubTable: React.FC<ExperienceTableProps> = ({
  data,
  isImpact = false,
  dataAgentTarget,
}) => {
  const tableData = isImpact ? data.tableImpact : data.tableBenefits;
  const subtitle = isImpact ? data.subtitle2 : data.subtitle1;
  const icon = isImpact ? arrowDownIcon : arrowUpIcon;

  if (tableData.length === 0) return null;

  const headers = [
    { label: "", flex: 0.4 },
    { label: data.likelyExperiencingTitle, flex: 1.4 },
    { label: data.underlyingMetricTitle, flex: 1.2 },
  ];

  return (
    <div
      style={{ flex: 1, marginBottom: "8px" }}
      data-agent-target={dataAgentTarget}
    >
      <div
        data-agent-target={`${dataAgentTarget}|title`}
        className={classes.tableCellRow}
        style={{ marginLeft: "16px", marginBottom: "4px" }}
      >
        <img
          src={icon}
          alt={isImpact ? "arrow down icon" : "arrow up icon"}
          style={{ height: "10px" }}
        />
        <div className={classes.subtitle}>{subtitle}</div>
      </div>
      <CheatsheetCustomCard>
        <TableHeader headers={headers} dataAgentTarget={dataAgentTarget} />
        {tableData.map((row: any, index: number) => (
          <div
            key={index}
            className={classes.row}
            data-agent-target={`${dataAgentTarget}|row_${row.name
              .toLowerCase()
              .replace(/ /g, "_")}`}
          >
            <div
              className={`${classes.tableCell} ${classes.tableCellBold}`}
              style={{ flex: 0.4 }}
            >
              <div>{row.name}</div>
            </div>
            <div className={classes.tableCell} style={{ flex: 1.4 }}>
              {row.likelyExperiencing}
            </div>
            <div className={classes.tableCell} style={{ flex: 1.2 }}>
              {row.underlyingMetric.join
                ? row.underlyingMetric.join(", ")
                : row.underlyingMetric}
            </div>
          </div>
        ))}
      </CheatsheetCustomCard>
    </div>
  );
};

const LikelyExperiencingTable = ({
  data,
  dataAgentTarget,
}: {
  data: any;
  dataAgentTarget?: string;
}) => {
  // Check if data is missing or empty
  if (!data) return null;

  // Check if both table sections are empty
  const hasBenefits = data.tableBenefits && data.tableBenefits.length > 0;
  const hasImpacts = data.tableImpact && data.tableImpact.length > 0;

  if (!hasBenefits && !hasImpacts) return null;

  return (
    <TableSection title={data.title} dataAgentTarget={dataAgentTarget}>
      <ExperienceSubTable
        data={data}
        dataAgentTarget={`${dataAgentTarget}|benefits_table`}
      />
      <ExperienceSubTable
        data={data}
        dataAgentTarget={`${dataAgentTarget}|impact_table`}
        isImpact={true}
      />
    </TableSection>
  );
};

interface MetricsTableProps {
  data: any;
  includeFactors?: boolean;
  dataAgentTarget?: string;
  customReportType?: string;
}

const MetricsTable: React.FC<MetricsTableProps> = ({
  data,
  includeFactors = false,
  dataAgentTarget,
  customReportType,
}) => {
  // Check if data or table is missing or empty
  if (!data || !data.table || data.table.length === 0) return null;

  const headers = [
    { label: data.metric, flex: 0.5 },
    { label: data.score, flex: 0.5 },
    { label: data.interpretation },
  ];

  if (customReportType === "restore" || customReportType === "restore1") {
    headers.push({ label: data.recommendation });
  }

  if (includeFactors) {
    headers.push({ label: data.potentialFactors });
  }

  return (
    <TableSection title={data.title} dataAgentTarget={dataAgentTarget}>
      <CheatsheetCustomCard>
        <TableHeader headers={headers} />
        {data.table.map((row: any, index: number) => (
          <div key={index} className={classes.row}>
            <div className={classes.tableCell} style={{ flex: 0.5 }}>
              {row.name}
            </div>
            <div className={classes.tableCell} style={{ flex: 0.5 }}>
              <div style={getColorCodingText(row.scoreType)}>{row.score}</div>
            </div>
            <div className={classes.tableCell}>
              {row.interpretation &&
                row.interpretation.map((text: string, index: number) => (
                  <div key={index} style={{ paddingBottom: "12px" }}>
                    {text}
                  </div>
                ))}
            </div>
            {(customReportType === "restore" ||
              customReportType === "restore1") && (
              <div className={classes.tableCell}>{row.recommendation}</div>
            )}
            {includeFactors && (
              <div className={classes.tableCell}>{row.potentialFactors}</div>
            )}
          </div>
        ))}
      </CheatsheetCustomCard>
    </TableSection>
  );
};

const TopStrengthsTable = ({
  data,
  dataAgentTarget,
  customReportType,
}: {
  data: any;
  dataAgentTarget?: string;
  customReportType?: string;
}) => {
  return (
    <MetricsTable
      data={data}
      dataAgentTarget={dataAgentTarget}
      customReportType={customReportType}
    />
  );
};

const AreasOfImprovementTable = ({
  data,
  dataAgentTarget,
  customReportType,
}: {
  data: any;
  dataAgentTarget?: string;
  customReportType?: string;
}) => {
  return (
    <MetricsTable
      data={data}
      includeFactors={true}
      dataAgentTarget={dataAgentTarget}
      customReportType={customReportType}
    />
  );
};

interface SimpleTableProps {
  data: any;
  headerFontSize?: string;
  marginBottom?: string;
  dataAgentTarget?: string;
  customReportType?: string;
}

const SimpleTable: React.FC<SimpleTableProps> = ({
  data,
  dataAgentTarget,
  headerFontSize = "14px",
  marginBottom = "8px",
  customReportType = "",
}) => {
  // Check if data or table is missing or empty
  if (!data || !data.table || data.table.length === 0) return null;

  // Extract header labels directly from data
  const getHeaders = () => {
    const headers = [];

    // Add headers based on what properties exist in data
    if (data.focus) headers.push({ label: data.focus });
    if (data.frequency) headers.push({ label: data.frequency });
    if (data.targetedGoal) headers.push({ label: data.targetedGoal });
    if (customReportType === "thedripbar" || customReportType === "restore1") {
      if (data.category) headers.push({ label: data.category });
    } else {
      if (data.priority) headers.push({ label: data.priority });
    }

    if (data.name) headers.push({ label: data.name });
    if (data.recommended) headers.push({ label: data.recommended });
    if (data.targeted) headers.push({ label: data.targeted });
    if (data.element) headers.push({ label: data.element, flex: 0.5 });
    if (data.prescription)
      headers.push({ label: data.prescription, flex: 0.5 });
    if (data.notes) headers.push({ label: data.notes });

    return headers;
  };

  const headerData = getHeaders();

  // If no headers were found, don't render the table
  if (headerData.length === 0) return null;

  // Function to render cell content based on type
  const renderCellContent = (
    row: any,
    key: string,
    index: number,
    rowIndex: number
  ) => {
    if (!row[key]) return null;

    if (Array.isArray(row[key])) {
      if (row[key].length === 0) return null;

      return row[key].map((item: any, idx: number) => {
        if (key === "prescription" && rowIndex === 0) {
          const index = item.indexOf(":");

          const part1 = item.slice(0, index); // Includes the colon
          const part2 = item.slice(index).trim(); // Rest of the string, trimmed

          return (
            <div key={idx} style={{ paddingBottom: "12px" }}>
              <span
                style={{
                  color: idx === 0 ? "var(--Good)" : "var(--Severe-limitation)",
                }}
              >
                {part1}{" "}
              </span>
              {part2}
            </div>
          );
        }

        return (
          <div key={idx} style={{ paddingBottom: "12px" }}>
            {item}
          </div>
        );
      });
    }

    if (key === "priority") {
      return <div style={getColorCodingText(row.priorityType)}>{row[key]}</div>;
    }

    return row[key];
  };

  return (
    <TableSection
      title={
        customReportType === "thedripbar" && data.title === "Bio-Optimization"
          ? "DRIPBaR Recommends"
          : data.title
      }
      titleFontSize={headerFontSize}
      marginBottom={marginBottom}
      dataAgentTarget={dataAgentTarget}
    >
      <CheatsheetCustomCard>
        <TableHeader headers={headerData} dataAgentTarget={dataAgentTarget} />
        {data.table.map((row: any, index: number) => (
          <div
            key={index}
            className={classes.row}
            data-agent-target={`${dataAgentTarget}|row_${
              row.element
                ? row.element.toLowerCase().replace(/ /g, "_")
                : row.name
                ? row.name.toLowerCase().replace(/ /g, "_")
                : row.focus
                ? row.focus.toLowerCase().replace(/ /g, "_")
                : ""
            }`}
          >
            {Object.keys(row).map((key, cellIndex) => {
              if (key === "priorityType" || key === "scoreType") return null;
              if (key === "category") return null;
              if (
                customReportType === "thedripbar" ||
                customReportType === "restore1"
              ) {
                if (key === "priority") {
                  key = "category";
                }
              }
              const style: any = {};
              if (key === "element" || key === "prescription") style.flex = 0.5;

              return (
                <div
                  key={cellIndex}
                  className={classes.tableCell}
                  style={style}
                >
                  {renderCellContent(row, key, cellIndex, index)}
                </div>
              );
            })}
          </div>
        ))}
      </CheatsheetCustomCard>
    </TableSection>
  );
};

const LifestyleWorkoutPlanTable = ({
  data,
  dataAgentTarget,
}: {
  data: any;
  dataAgentTarget?: string;
}) => {
  return <SimpleTable dataAgentTarget={dataAgentTarget} data={data} />;
};

const NutritionPlanTable = ({
  data,
  dataAgentTarget,
}: {
  data: any;
  dataAgentTarget?: string;
}) => {
  return <SimpleTable dataAgentTarget={dataAgentTarget} data={data} />;
};

const BioOptimizationPlanTable = ({
  data,
  customReportType,
  dataAgentTarget,
}: {
  data: any;
  customReportType: string;
  dataAgentTarget?: string;
}) => {
  return (
    <SimpleTable
      dataAgentTarget={dataAgentTarget}
      customReportType={customReportType}
      data={data}
    />
  );
};

const RMRCheatsheetPage = ({
  data,
  customReportType,
  reportType,
  showRestoreServiceSection,
}: {
  data: any;
  customReportType?: string;
  reportType?: string;
  showRestoreServiceSection?: boolean;
}) => {
  // Check if a Personalized Prescription section has any data
  const hasPersonalizedData =
    (data.lifestyleWorkoutPlanTable &&
      data.lifestyleWorkoutPlanTable.table &&
      data.lifestyleWorkoutPlanTable.table.length > 0) ||
    (data.nutritionPlanTable &&
      data.nutritionPlanTable.table &&
      data.nutritionPlanTable.table.length > 0) ||
    (data.bioOptimizationTable &&
      data.bioOptimizationTable.table &&
      data.bioOptimizationTable.table.length > 0);

  return (
    <>
      {reportType === "19" && (
        <Layout>
          <div style={{ height: "10px" }} />
          {/* System Overview Section */}
          <SystemOverviewTable
            data={data.systemOverviewTable}
            dataAgentTarget="table_system_overview"
          />

          {/* Experience Tables Section */}
          <LikelyExperiencingTable
            data={data.likelyExperiencingTable}
            dataAgentTarget={"table_likely_experiencing"}
          />
        </Layout>
      )}
      {reportType === "19" && (
        <Layout dataAgentTarget="box-5">
          <div style={{ height: "20px" }} />
          {/* Strengths & Areas for Improvement */}
          <TopStrengthsTable
            data={data.topStrengthsTable}
            dataAgentTarget={"table_top_strengths"}
            customReportType={customReportType}
          />
          <AreasOfImprovementTable
            data={data.areasOfImprovementTable}
            dataAgentTarget={"table_areas_of_improvement"}
            customReportType={customReportType}
          />
        </Layout>
      )}
      {/* Personalized Prescription Section - only show if it has data */}
      {hasPersonalizedData && (
        <Layout dataAgentTarget="box-6">
          <div className={classes.title} style={{ paddingTop: 20 }}>
            {data.personalizedPrescriptionTitle}
          </div>
          <LifestyleWorkoutPlanTable
            data={data.lifestyleWorkoutPlanTable}
            dataAgentTarget={"table_lifestyle_workout_plan"}
          />
          <NutritionPlanTable
            data={data.nutritionPlanTable}
            dataAgentTarget={"table_nutrition_plan"}
          />
          <BioOptimizationPlanTable
            data={data.bioOptimizationTable}
            dataAgentTarget={"table_bio_optimization_plan"}
            customReportType={customReportType ?? ""}
          />
          {showRestoreServiceSection && (
            <>
              <div
                style={{
                  marginTop: "40px",
                  textAlign: "center",
                  fontSize: "14px",
                }}
              >
                Increasing NAD+ levels may help slow the aging process and
                reduce the risk of age-related diseases.
              </div>
              <ServiceSection
                data={restoreNadServiceData}
                type="restoreCustomReport"
              />
            </>
          )}
        </Layout>
      )}
    </>
  );
};

export default RMRCheatsheetPage;
